import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  PurchaseOrderRequest,
  PurchaseOrderHeader,
  GlobalCostCenter,
  PurchaseOrderItem,
  CostCenterStrategy,
  MockSupplier,
  MockProject,
  MockWorkCategory
} from '../components/PurchaseOrders/PurchaseOrderFormData';
import { PurchaseOrderHeaderForm } from '../components/PurchaseOrders/PurchaseOrderHeaderForm';
import { CostCenterStrategySelector } from '../components/PurchaseOrders/CostCenterStrategySelector';
import { GlobalCostCenterPanel } from '../components/PurchaseOrders/GlobalCostCenterPanel';
import { PurchaseItemsTable } from '../components/PurchaseOrders/PurchaseItemsTable';
import { OrderSummary } from '../components/PurchaseOrders/OrderSummary';
import { supplierService } from '../services/supplierService';
import { projectService } from '../services/projectService';
import { generalWorkCategoryService } from '../services/generalWorkCategoryService';
import { purchaseOrderService } from '../services/purchaseOrderService';
import { ArrowLeft, Save, AlertCircle } from 'lucide-react';
import RequirePermission from '../components/RequirePermission';
import { PERMISSIONS } from '../constants/permissions';
import { showError } from '../components/Alerts';
import LoadingScreen from '../components/ui/LoadingScreen';

const PurchaseOrderEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  // Datos cargados del backend
  const [suppliers, setSuppliers] = useState<MockSupplier[]>([]);
  const [projects, setProjects] = useState<MockProject[]>([]);
  const [generalWorkCategories, setGeneralWorkCategories] = useState<MockWorkCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Estado del formulario
  const [header, setHeader] = useState<PurchaseOrderHeader>({
    supplierId: 0,
    orderDate: new Date().toISOString().split('T')[0],
    accountingType: 'A',
    ivaCode: '21',
    currency: 'ARS',
  });

  const [strategy, setStrategy] = useState<CostCenterStrategy>('GLOBAL');
  const [globalCostCenter, setGlobalCostCenter] = useState<GlobalCostCenter | null>({
    costCenterType: 'PROJECT',
    projectId: null,
    generalCostCenterId: null,
    workCategoryId: 0,
  });

  const [items, setItems] = useState<PurchaseOrderItem[]>([]);
  const [orderNumber, setOrderNumber] = useState<string>('');

  // Cargar datos del backend al montar el componente
  useEffect(() => {
    const loadData = async () => {
      if (!id) {
        setLoadError('ID de orden no válido');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadError(null);
      
      try {
        // Cargar proveedores, proyectos, rubros generales y la orden en paralelo
        const [suppliersData, projectsData, generalCategoriesData, orderData] = await Promise.all([
          supplierService.getAllSuppliers(),
          projectService.getAllSimple(),
          generalWorkCategoryService.getAllGeneralWorkCategories(),
          purchaseOrderService.getPurchaseOrderById(Number(id))
        ]);

        // Mapear proveedores
        const mappedSuppliers: MockSupplier[] = suppliersData.map(s => ({
          id: s.id,
          name: s.businessName,
          cuit: s.cuit
        }));
        setSuppliers(mappedSuppliers);

        // Mapear proyectos
        const mappedProjects: MockProject[] = projectsData.map(p => ({
          id: p.id,
          name: p.name,
          workCategories: p.workCategories.map(wc => ({
            id: wc.id,
            name: wc.value
          }))
        }));
        setProjects(mappedProjects);

        // Mapear rubros generales
        const mappedGeneralCategories: MockWorkCategory[] = generalCategoriesData.map(gc => ({
          id: gc.id,
          name: gc.name,
          isSystemDefault: gc.isSystemDefault
        }));
        setGeneralWorkCategories(mappedGeneralCategories);

        // Mapear datos de la orden al formulario
        setOrderNumber(orderData.orderNumber);
        
        setHeader({
          supplierId: orderData.supplierId,
          orderDate: orderData.orderDate,
          accountingType: orderData.accountingType as 'A' | 'B',
          ivaCode: orderData.ivaCode,
          currency: orderData.currency as 'ARS' | 'USD',
        });

        setStrategy(orderData.costCenterStrategy);

        if (orderData.globalCostCenter) {
          setGlobalCostCenter({
            costCenterType: orderData.globalCostCenter.costCenterType,
            projectId: orderData.globalCostCenter.projectId,
            generalCostCenterId: orderData.globalCostCenter.generalCostCenterId,
            workCategoryId: orderData.globalCostCenter.workCategoryId,
          });
        } else {
          setGlobalCostCenter(null);
        }

        // Mapear items
        const mappedItems: PurchaseOrderItem[] = orderData.items.map(item => ({
          id: item.id.toString(),
          description: item.description,
          unit: item.unit,
          unitPrice: item.unitPrice,
          quantity: orderData.costCenterStrategy === 'PER_LINE' ? undefined : item.quantity,
          allocations: item.allocations.map(alloc => ({
            costCenterType: alloc.costCenterType,
            projectId: alloc.projectId,
            generalCostCenterId: alloc.generalCostCenterId,
            workCategoryId: alloc.workCategoryId,
            allocatedQuantity: alloc.allocatedQuantity,
          }))
        }));
        setItems(mappedItems);

        setIsLoading(false);
      } catch (error) {
        console.error('Error cargando datos:', error);
        setLoadError('Error al cargar los datos de la orden. Por favor, recargue la página.');
        setIsLoading(false);
      }
    };

    loadData();
  }, [id]);

  // Validaciones
  const validateForm = (): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Validar header
    if (header.supplierId === 0) {
      errors.push('Debe seleccionar un proveedor');
    }
    if (!header.orderDate) {
      errors.push('Debe ingresar la fecha de orden');
    }
    if (header.accountingType === 'A' && !header.ivaCode) {
      errors.push('Debe seleccionar el tipo de IVA');
    }

    // Validar estrategia
    if (strategy === 'GLOBAL') {
      if (!globalCostCenter) {
        errors.push('Debe configurar el centro de costo global');
      } else {
        if (globalCostCenter.costCenterType === 'PROJECT' && !globalCostCenter.projectId) {
          errors.push('Debe seleccionar un proyecto');
        }
        if (!globalCostCenter.workCategoryId) {
          errors.push('Debe seleccionar un rubro');
        }
      }
    }

    // Validar items
    if (items.length === 0) {
      errors.push('Debe agregar al menos un item');
    }

    items.forEach((item, index) => {
      if (!item.description.trim()) {
        errors.push(`Item ${index + 1}: La descripción es obligatoria`);
      }
      if (item.unitPrice <= 0) {
        errors.push(`Item ${index + 1}: El precio unitario debe ser mayor a 0`);
      }

      if (strategy === 'GLOBAL' || strategy === 'UNASSIGNED') {
        if (!item.quantity || item.quantity <= 0) {
          errors.push(`Item ${index + 1}: La cantidad debe ser mayor a 0`);
        }
      } else {
        // PER_LINE
        if (item.allocations.length === 0) {
          errors.push(`Item ${index + 1}: Debe tener al menos una asignación`);
        }
        item.allocations.forEach((alloc, allocIndex) => {
          if (alloc.costCenterType === 'PROJECT' && !alloc.projectId) {
            errors.push(`Item ${index + 1}, Asignación ${allocIndex + 1}: Debe seleccionar un proyecto`);
          }
          if (!alloc.workCategoryId) {
            errors.push(`Item ${index + 1}, Asignación ${allocIndex + 1}: Debe seleccionar un rubro`);
          }
          if (alloc.allocatedQuantity <= 0) {
            errors.push(`Item ${index + 1}, Asignación ${allocIndex + 1}: La cantidad debe ser mayor a 0`);
          }
        });
      }
    });

    return { valid: errors.length === 0, errors };
  };

  const handleSave = async () => {
    const validation = validateForm();
    
    if (!validation.valid) {
      showError('Por favor corrija los siguientes errores:\n' + validation.errors.join('\n'));
      return;
    }

    // Construir el JSON de la orden (igual que en create)
    const purchaseOrder: PurchaseOrderRequest = {
      supplierId: header.supplierId,
      orderDate: header.orderDate,
      accountingType: header.accountingType,
      ivaCode: header.ivaCode,
      currency: header.currency,
      costCenterStrategy: strategy,
      globalCostCenter: strategy === 'GLOBAL' ? globalCostCenter : null,
      items: items,
    };

    setIsSaving(true);

    try {
      await purchaseOrderService.updatePurchaseOrder(Number(id), purchaseOrder);
      navigate('/purchase-orders');
    } catch (error: any) {
      console.error('Error al actualizar orden de compra:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Error desconocido al actualizar la orden';
      showError('Error al actualizar la orden de compra: ' + errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleStrategyChange = (newStrategy: CostCenterStrategy) => {
    setStrategy(newStrategy);
    
    // Si cambia a GLOBAL, inicializar globalCostCenter
    if (newStrategy === 'GLOBAL' && !globalCostCenter) {
      setGlobalCostCenter({
        costCenterType: 'PROJECT',
        projectId: null,
        generalCostCenterId: null,
        workCategoryId: 0,
      });
    }
    
    // Si cambia a PER_LINE, limpiar las cantidades globales de los items
    if (newStrategy === 'PER_LINE') {
      setItems(items.map(item => ({ ...item, quantity: undefined })));
    } else {
      // Si cambia a GLOBAL o UNASSIGNED, agregar cantidad 1 a cada item y limpiar allocations
      setItems(items.map(item => ({ ...item, quantity: item.quantity || 1, allocations: [] })));
    }
  };

  if (isLoading) return <LoadingScreen message="Cargando orden de compra..." />;
  if (isSaving) return <LoadingScreen message="Guardando..." />;

  // Mostrar error
  if (loadError) {
    return (
      <div className="w-full h-full p-6">
        <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
          <div className="p-6">
            <div className="flex items-start gap-3 p-4 border rounded-lg bg-destructive/10 border-destructive/20">
              <AlertCircle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-foreground">Error al cargar datos</h3>
                <p className="mt-1 text-sm text-muted-foreground">{loadError}</p>
                <div className="flex gap-2 mt-3">
                  <Button onClick={() => window.location.reload()} variant="destructive" size="sm">
                    Recargar página
                  </Button>
                  <Button onClick={() => navigate('/purchase-orders')} variant="outline" size="sm">
                    Volver al listado
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full p-4 space-y-6 sm:p-6">
      {/* Header */}
      <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
        <div className="px-4 py-5 border-b sm:px-6 border-border">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/purchase-orders')}
                className="flex items-center gap-2 rounded-lg"
              >
                <ArrowLeft className="w-4 h-4" />
                Volver
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Editar Orden de Compra</h1>
                <p className="mt-1 text-sm text-muted-foreground">Modificar orden {orderNumber}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 border-b sm:px-6 border-border bg-muted/30">
          <div className="flex items-center gap-2">
            <AlertCircle className="flex-shrink-0 w-4 h-4 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">
              Modifique los campos necesarios y guarde los cambios. Los campos marcados con{' '}
              <span className="font-semibold text-foreground">*</span> son obligatorios.
            </p>
          </div>
        </div>
      </div>

      {/* Form Sections */}
      <PurchaseOrderHeaderForm 
        header={header} 
        onChange={setHeader}
        suppliers={suppliers}
      />

      <CostCenterStrategySelector strategy={strategy} onChange={handleStrategyChange} />

      {strategy === 'GLOBAL' && globalCostCenter && (
        <GlobalCostCenterPanel 
          costCenter={globalCostCenter} 
          onChange={setGlobalCostCenter}
          projects={projects}
          generalWorkCategories={generalWorkCategories}
        />
      )}

      <PurchaseItemsTable
        items={items}
        strategy={strategy}
        currency={header.currency === 'USD' ? 'USD' : '$'}
        onChange={setItems}
        projects={projects}
        generalWorkCategories={generalWorkCategories}
      />

      {items.length > 0 && (
        <OrderSummary
          items={items}
          strategy={strategy}
          globalCostCenter={globalCostCenter}
          currency={header.currency === 'USD' ? 'USD' : '$'}
          projects={projects}
          generalWorkCategories={generalWorkCategories}
          accountingType={header.accountingType}
          ivaCode={header.ivaCode}
        />
      )}

      {/* Bottom Actions */}
      <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
        <div className="flex flex-col-reverse gap-3 px-4 py-4 sm:px-6 sm:flex-row sm:justify-end border-border">
          <Button
            onClick={() => navigate('/purchase-orders')}
            variant="outline"
            className="w-full rounded-lg sm:w-auto"
          >
            Cancelar
          </Button>
          <RequirePermission permission={PERMISSIONS.COMPRAS.OC_EDITAR}>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full rounded-lg bg-primary text-primary-foreground hover:opacity-90 sm:w-auto"
            >
              <Save className="w-4 h-4 mr-2" />
              Guardar Cambios
            </Button>
          </RequirePermission>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderEdit;
